/**
 * AdventureTitleImage Component
 * 
 * Displays an adventure title image with WebP/JPEG fallback support and lazy loading.
 * Used in adventure selection, lobby, and join screens.
 * Aspect ratio: 3:1 (wide landscape)
 */

import React, { useState, useEffect } from 'react';
import { imageService } from '../../services/imageService';
import type { ImageVariantSize } from '../../services/imageService';
import { resolveAdventureTitleImageId } from '../../utils/adventureTitleImageId';
import useGameStore from '../../stores/gameStore';
import { usePictureImageLoad } from '../../hooks/usePictureImageLoad';
import PictureImageContent from './PictureImageContent';
import LoadingSpinner from './LoadingSpinner';

export interface AdventureTitleImageProps {
  /**
   * Title image ID to display (e.g., 'fantasy', 'mystery', 'generic')
   * If undefined or not found, falls back to 'generic'
   */
  titleImageId?: string | undefined;

  /**
   * Adventure ID for resolving unqualified title image IDs to adventure-specific
   * namespaced IDs (e.g. template_event → events/template/host:template_event).
   */
  adventureId?: string | undefined;
  
  /**
   * Size variant to display
   * - thumbnail: 768×256px (for smaller displays)
   * - card: 1536×512px (for adventure cards, selection)
   * - large: 2304×768px (for detail views)
   */
  variant?: ImageVariantSize;
  
  /**
   * Alt text for the image (for accessibility)
   */
  alt?: string;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Custom fallback element to display if image fails to load
   */
  fallback?: React.ReactNode;
  
  /**
   * Whether to use lazy loading (default: true)
   */
  lazy?: boolean;
  
  /**
   * If true, makes the image responsive (width: 100%, height: auto)
   * instead of using fixed size classes
   */
  responsive?: boolean;
}

/**
 * Default fallback component when title image cannot be loaded
 */
const DefaultFallback: React.FC<{ variant: ImageVariantSize; alt: string | undefined; responsive?: boolean }> = ({ variant, alt, responsive = false }) => {
  const sizes = responsive ? {
    thumbnail: 'w-full',
    card: 'w-full',
    large: 'w-full',
  } : {
    thumbnail: 'w-96 h-32',
    card: 'w-[48rem] h-64',
    large: 'w-[72rem] h-96',
  };
  
  const iconSizes = {
    thumbnail: 'text-4xl',
    card: 'text-8xl',
    large: 'text-9xl',
  };

  return (
    <div 
      className={`${sizes[variant]} flex items-center justify-center bg-gray-700 text-gray-400 overflow-hidden`}
      style={{
        borderRadius: '8px',
        border: '1px solid #374151',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)',
        ...(responsive ? { aspectRatio: '3 / 1' } : {})
      }}
      role="img"
      aria-label={alt || 'Adventure title image placeholder'}
    >
      <span className={iconSizes[variant]}>🗺️</span>
    </div>
  );
};

const resolveLoadingSizeClasses = (responsive: boolean) =>
  responsive
    ? { thumbnail: 'w-full', card: 'w-full', large: 'w-full' }
    : { thumbnail: 'w-96 h-32', card: 'w-[48rem] h-64', large: 'w-[72rem] h-96' };

/**
 * AdventureTitleImage component
 */
export const AdventureTitleImage: React.FC<AdventureTitleImageProps> = ({
  titleImageId,
  adventureId,
  variant = 'card',
  alt,
  className = '',
  fallback,
  lazy = true,
  responsive = false,
}) => {
  const sessionAdventureId = useGameStore(
    (state) =>
      state.currentAdventure?.id
      ?? state.selectedAdventureId
      ?? state.currentSession?.adventureId
      ?? undefined
  );
  const effectiveAdventureId = adventureId ?? sessionAdventureId;

  const [resolvedTitleImageId, setResolvedTitleImageId] = useState<string | undefined>(undefined);
  const [resolveReady, setResolveReady] = useState(!effectiveAdventureId);

  useEffect(() => {
    let cancelled = false;

    const resolveId = async () => {
      if (!effectiveAdventureId) {
        if (!cancelled) {
          setResolvedTitleImageId(titleImageId);
          setResolveReady(true);
        }
        return;
      }

      if (!cancelled) {
        setResolveReady(false);
      }

      try {
        await imageService.loadAvailableIds();
      } catch {
        // Resolver falls back to header value when IDs are unavailable
      }

      if (!cancelled) {
        setResolvedTitleImageId(
          resolveAdventureTitleImageId(
            effectiveAdventureId,
            titleImageId,
            imageService.getTitleImageIds()
          )
        );
        setResolveReady(true);
      }
    };

    void resolveId();

    return () => {
      cancelled = true;
    };
  }, [effectiveAdventureId, titleImageId]);

  const activeTitleImageId =
    resolveReady && resolvedTitleImageId !== undefined ? resolvedTitleImageId : undefined;

  const baseUrls = activeTitleImageId
    ? imageService.getTitleImageUrls(activeTitleImageId, variant)
    : null;
  const identityKey = `${activeTitleImageId ?? 'pending'}-${variant}`;

  const {
    imgRef,
    isLoading,
    failedUrl,
    imageUrls,
    pictureKey,
    handleLoad,
    handleError,
  } = usePictureImageLoad(baseUrls, identityKey, {
    enabled: Boolean(activeTitleImageId),
    lazy,
    onLoadFailed: () => {
      console.warn(`Failed to load title image: ${activeTitleImageId}`);
    },
  });

  if (!activeTitleImageId) {
    const sizeClasses = resolveLoadingSizeClasses(responsive);

    return (
      <div
        className={`${sizeClasses[variant]} relative ${className}`}
        style={{
          borderRadius: '8px',
          border: '1px solid #374151',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          ...(responsive ? { aspectRatio: '3 / 1' } : {}),
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center bg-gray-700" aria-busy="true" aria-label="Loading image">
          <LoadingSpinner size="sm" color="white" />
        </div>
      </div>
    );
  }

  if (!imageUrls || failedUrl === imageUrls.jpg) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <DefaultFallback variant={variant} alt={alt} responsive={responsive} />
    );
  }

  const sizeClasses = resolveLoadingSizeClasses(responsive);

  return (
    <div 
      className={`${sizeClasses[variant]} relative ${className}`}
      style={{
        borderRadius: '8px',
        border: '1px solid #374151',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
        backgroundColor: 'transparent',
        ...(responsive ? { aspectRatio: '3 / 1' } : {})
      }}
    >
      <PictureImageContent
        imageUrls={imageUrls}
        pictureKey={pictureKey}
        imgRef={imgRef}
        alt={alt || 'Adventure title image'}
        lazy={lazy}
        isLoading={isLoading}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
};

export default AdventureTitleImage;
